import {requirePermission} from '@/modules/auth/permissions'
import {useGetInfiniteUserGroups} from '@/modules/user-groups/api'
import {UserGroupsFilters, UserGroupsTable} from '@/modules/user-groups/components'
import {UserGroupsSearchSchema} from '@/modules/user-groups/schemas'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {ButtonGroup, HStack} from '@chakra-ui/react'
import {createFileRoute} from '@tanstack/react-router'
import {useTranslate} from '@tolgee/react'
import {useCallback} from 'react'
import {LuPlus} from 'react-icons/lu'
import {ResourcesIndexPageTable} from '@/shared/components/ui/resources-index-page-table'
import {type OnChangeFn, type SortingState} from '@tanstack/react-table'

const LIMIT = 50

export const Route = createFileRoute('/configuration/user-groups/')({
  beforeLoad: requirePermission('users.manage'),
  component: UserGroupsPage,
  validateSearch: UserGroupsSearchSchema,
})

function UserGroupsPage() {
  const {t} = useTranslate('common')
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const [sort] = search.sort

  const infiniteGroups = useGetInfiniteUserGroups({
    limit: LIMIT,
    sort: {
      dir: sort?.desc ? 'desc' : 'asc',
      field: sort?.id || 'createdAt',
    },
    filters: {search: search.filters?.search, status: []},
  })

  const {getPageQuery, getSingleQuery, toStartRow, listContextParams} = infiniteGroups

  const onSortingChange = useCallback<OnChangeFn<SortingState>>(
    updaterOrValue => {
      const newValue = typeof updaterOrValue === 'function' ? updaterOrValue(search.sort) : updaterOrValue
      navigate({to: '.', search: prev => ({...prev, sort: newValue})})
    },
    [navigate, search.sort]
  )

  return (
    <>
      <HStack justifyContent="space-between">
        <ButtonGroup size="sm" display="flex" width="100%" marginY="1rem">
          <LinkButton to="/configuration/user-groups/new" colorPalette="cyan">
            <LuPlus />
            {t('add_new')}
          </LinkButton>
        </ButtonGroup>

        <UserGroupsFilters />
      </HStack>
      <ResourcesIndexPageTable>
        <UserGroupsTable
          virtualizer={{getPageQuery, getSingleQuery, toStartRow, listContextParams}}
          table={{onSortingChange, state: {sorting: search.sort}}}
        />
      </ResourcesIndexPageTable>
    </>
  )
}
